import { computed, ComputedRef, useAttrs } from 'vue';

/**
 * A Vue composable that filters and returns namespaced attributes from the component's attributes.
 *
 * @function
 * @name useHNamespaceProps
 * @template T
 * @param {string} prefix - The prefix to filter the attributes by.
 * @returns {ComputedRef<T>} - A computed reference containing the filtered attributes with the prefix removed.
 * @description
 * This composable function takes a prefix string and returns a computed reference of the component's attributes
 * that start with the given prefix. The prefix is removed from the keys of the returned attributes.
 */
export function useHNamespaceProps<T extends Object>(prefix: string): ComputedRef<T> {
  const attrs = useAttrs();
  const _prefix = prefix + ':';

  return computed(() =>
    Object.keys(attrs)
      .filter((key) => key.startsWith(_prefix))
      .reduce((acc, key) => {
        Object.assign(acc, { [key.replace(_prefix, '')]: attrs[key] });

        return acc;
      }, {} as T),
  );
}
